import React, { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { MessageSquare, Upload, Loader2, CheckCircle, AlertCircle, FileDown, X, Users, Building2, Package, Calendar, TrendingUp } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const WhatsAppAnalysis = () => {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const onDrop = useCallback((acceptedFiles) => {
    const validFile = acceptedFiles.find(f => 
      f.name.endsWith('.zip') || f.name.endsWith('.txt')
    );
    
    if (!validFile) {
      toast.error('يجب رفع ملف ZIP أو TXT من تصدير واتساب');
      return;
    }
    
    setFile(validFile);
    setResult(null);
    setError(null);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/zip': ['.zip'],
      'text/plain': ['.txt']
    },
    multiple: false
  });

  const handleUpload = async () => {
    if (!file) {
      toast.error('الرجاء اختيار ملف المحادثة');
      return;
    }

    setUploading(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(`${API_URL}/api/analysis/whatsapp/upload`, {
        method: 'POST',
        body: formData
      });

      const data = await response.json();

      if (data.success) {
        setResult(data);
        toast.success(`تم تحليل ${data.total_orders} طلب من ${data.total_supervisors} مشرف`);
      } else {
        throw new Error(data.detail || 'فشل في تحليل المحادثة');
      }
    } catch (err) {
      setError(err.message);
      toast.error(err.message);
    } finally {
      setUploading(false);
    }
  };

  const handleExport = async () => {
    if (!result?.task_id) {
      toast.error('الرجاء رفع وتحليل الملف أولاً');
      return;
    }

    setExporting(true);

    try {
      const response = await fetch(`${API_URL}/api/analysis/whatsapp/export-excel`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ task_id: result.task_id })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'فشل في تصدير الملف');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `تحليل_طلبات_واتساب_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      toast.success('تم تصدير التقرير بنجاح');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setExporting(false);
    }
  };

  const resetForm = () => {
    setFile(null);
    setResult(null);
    setError(null);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-green-900 to-slate-900 p-6" dir="rtl">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-green-500 to-emerald-600 mb-4 shadow-lg shadow-green-500/25">
            <MessageSquare className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">تحليل طلبات واتساب</h1>
          <p className="text-slate-400">تحليل محادثات واتساب لاستخراج طلبات المشتريات</p>
        </div>

        {/* Info Box */}
        <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl p-4 mb-6 border border-slate-700">
          <div className="flex items-start gap-3">
            <div className="p-2 rounded-lg bg-green-500/20">
              <MessageSquare className="w-5 h-5 text-green-400" />
            </div>
            <div>
              <h3 className="text-white font-medium mb-1">تحليل ذكي للمحادثات</h3>
              <p className="text-slate-400 text-sm">
                ارفع ملف تصدير محادثة واتساب (ZIP أو TXT) لاستخراج وتحليل طلبات المشتريات
              </p>
              <p className="text-slate-500 text-xs mt-1">
                * يدعم استخراج: رقم المشروع، اسم المشرف، الأصناف والكميات، التواريخ
              </p>
            </div>
          </div>
        </div>

        {/* Upload Area */}
        <div className="bg-slate-800/50 backdrop-blur-sm rounded-2xl p-6 border border-slate-700 mb-6">
          <div
            {...getRootProps()}
            data-testid="whatsapp-dropzone"
            className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all duration-300 ${
              isDragActive
                ? 'border-green-500 bg-green-500/10'
                : file
                ? 'border-green-500/50 bg-green-500/5'
                : 'border-slate-600 hover:border-slate-500 hover:bg-slate-700/30'
            }`}
          >
            <input {...getInputProps()} data-testid="whatsapp-file-input" />
            
            <div className="flex flex-col items-center">
              <div className="w-14 h-14 rounded-xl bg-slate-700 flex items-center justify-center mb-3">
                <Upload className="w-7 h-7 text-slate-400" />
              </div>
              <p className="text-white font-medium mb-1">
                {isDragActive ? 'أفلت الملف هنا' : 'اسحب ملف محادثة واتساب هنا'}
              </p>
              <p className="text-slate-400 text-sm">أو اضغط لاختيار ملف</p>
              <p className="text-slate-500 text-xs mt-2">يدعم ملفات ZIP و TXT من تصدير واتساب</p>
            </div>
          </div>

          {/* File Info */}
          {file && (
            <div className="mt-4">
              <div className="flex items-center justify-between bg-slate-700/50 rounded-lg p-3">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-green-500/20 flex items-center justify-center">
                    <MessageSquare className="w-4 h-4 text-green-400" />
                  </div>
                  <div>
                    <p className="text-white text-sm">{file.name}</p>
                    <p className="text-slate-500 text-xs">{(file.size / 1024 / 1024).toFixed(2)} MB</p>
                  </div>
                </div>
                <button
                  onClick={() => setFile(null)}
                  className="p-1 hover:bg-slate-600 rounded transition-colors"
                >
                  <X className="w-4 h-4 text-slate-400" />
                </button>
              </div>
            </div>
          )}

          {/* Upload Button */}
          {file && !result && (
            <button
              onClick={handleUpload}
              disabled={uploading}
              className="w-full mt-4 py-3 px-4 rounded-xl bg-gradient-to-r from-green-500 to-emerald-600 text-white font-medium hover:from-green-600 hover:to-emerald-700 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              data-testid="whatsapp-upload-btn"
            >
              {uploading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  جاري التحليل...
                </>
              ) : (
                <>
                  <MessageSquare className="w-5 h-5" />
                  تحليل المحادثة
                </>
              )}
            </button>
          )}
        </div>

        {/* Error */}
        {error && (
          <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 mb-6 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-red-400 font-medium">خطأ في التحليل</p>
              <p className="text-red-300/70 text-sm">{error}</p>
            </div>
          </div>
        )}

        {/* Results */}
        {result && (
          <div className="bg-slate-800/50 backdrop-blur-sm rounded-2xl p-6 border border-slate-700 space-y-6">
            {/* Success Header */}
            <div className="flex items-center gap-3 pb-4 border-b border-slate-700">
              <div className="w-10 h-10 rounded-xl bg-green-500/20 flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-green-400" />
              </div>
              <div>
                <p className="text-white font-medium">تم التحليل بنجاح</p>
                <p className="text-slate-400 text-sm">جاهز لتصدير تقرير الطلبات</p>
              </div>
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-slate-700/50 rounded-xl p-4 text-center">
                <div className="flex items-center justify-center mb-2">
                  <Package className="w-5 h-5 text-green-400" />
                </div>
                <p className="text-2xl font-bold text-green-400">{result.total_orders}</p>
                <p className="text-slate-400 text-sm">إجمالي الطلبات</p>
              </div>
              <div className="bg-slate-700/50 rounded-xl p-4 text-center">
                <div className="flex items-center justify-center mb-2">
                  <Building2 className="w-5 h-5 text-blue-400" />
                </div>
                <p className="text-2xl font-bold text-blue-400">{result.total_projects}</p>
                <p className="text-slate-400 text-sm">مشروع</p>
              </div>
              <div className="bg-slate-700/50 rounded-xl p-4 text-center">
                <div className="flex items-center justify-center mb-2">
                  <Users className="w-5 h-5 text-purple-400" />
                </div>
                <p className="text-2xl font-bold text-purple-400">{result.total_supervisors}</p>
                <p className="text-slate-400 text-sm">مشرف</p>
              </div>
              <div className="bg-slate-700/50 rounded-xl p-4 text-center">
                <div className="flex items-center justify-center mb-2">
                  <TrendingUp className="w-5 h-5 text-amber-400" />
                </div>
                <p className="text-2xl font-bold text-amber-400">{result.total_items}</p>
                <p className="text-slate-400 text-sm">صنف</p>
              </div>
            </div>

            {/* Date Range */}
            {result.date_range && (
              <div className="bg-slate-700/30 rounded-xl p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Calendar className="w-4 h-4 text-slate-400" />
                  <p className="text-slate-400 text-sm">الفترة الزمنية</p>
                </div>
                <p className="text-white">
                  من <span className="text-green-400">{result.date_range.start}</span> إلى <span className="text-green-400">{result.date_range.end}</span>
                </p>
              </div>
            )}

            {/* Top Supervisors */}
            {result.top_supervisors && result.top_supervisors.length > 0 && (
              <div className="space-y-2">
                <p className="text-slate-400 text-sm">أكثر المشرفين طلبات:</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {result.top_supervisors.slice(0, 6).map((sup, idx) => (
                    <div key={idx} className="flex items-center gap-3 bg-slate-700/30 rounded-lg p-3">
                      <div className="w-8 h-8 rounded-lg bg-purple-500/20 flex items-center justify-center text-purple-400 text-sm font-bold">
                        {idx + 1}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-white text-sm truncate">{sup.name}</p>
                        <p className="text-slate-500 text-xs">{sup.orders_count} طلب</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Top Projects */}
            {result.top_projects && result.top_projects.length > 0 && (
              <div className="space-y-2">
                <p className="text-slate-400 text-sm">أكثر المشاريع طلبات:</p>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  {result.top_projects.slice(0, 6).map((proj, idx) => (
                    <div key={idx} className="flex items-center gap-2 bg-slate-700/30 rounded-lg p-2">
                      <div className="w-6 h-6 rounded bg-blue-500/20 flex items-center justify-center text-blue-400 text-xs font-bold">
                        {idx + 1}
                      </div>
                      <div>
                        <p className="text-white text-sm">مشروع {proj.project}</p>
                        <p className="text-slate-500 text-xs">{proj.orders_count} طلب</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Export Button */}
            <button
              onClick={handleExport}
              disabled={exporting}
              className="w-full py-3 px-4 rounded-xl bg-gradient-to-r from-green-500 to-emerald-600 text-white font-medium hover:from-green-600 hover:to-emerald-700 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              data-testid="whatsapp-export-btn"
            >
              {exporting ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  جاري التصدير...
                </>
              ) : (
                <>
                  <FileDown className="w-5 h-5" />
                  تصدير تقرير الطلبات (Excel)
                </>
              )}
            </button>

            {/* Reset */}
            <button
              onClick={resetForm}
              className="w-full py-2 text-slate-400 hover:text-white transition-colors text-sm"
            >
              تحليل محادثة جديدة
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default WhatsAppAnalysis;
